/**
 * Set or change your own password.
 *
 * The in-app half of password management: a signed-in member changing a
 * password they still know. The other half is the emailed reset link
 * (/forgot → /reset/:token), which is what a member who CANNOT sign in uses.
 * Both end in the same place, so both go through passwordProblem().
 */

import { useState } from 'react';
import { PASSWORD_MIN, passwordProblem } from '../../data';
import { useClub } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { Button, Field, FormError, Hint, SheetActions, SheetHead } from '../ui';

export function PasswordSheet({ member }) {
  const { setOwnPassword } = useClub();
  const { closeSheet, toast } = useUI();

  /*
   * Whether a password EXISTS, not what it is — the browser never sees one.
   * Firebase Auth is the authority; `hasPassword` is the flag the API keeps
   * true on every path that sets one, so this sheet knows whether to ask for
   * the current password or offer to set a first.
   */
  const hasPassword = Boolean(member.hasPassword);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    // Only ask for the old one when there IS an old one — the members who
    // need this sheet most are exactly the ones who have never had a password.
    // The check itself is the server's: it is the only party that can make it.
    if (hasPassword && !current) {
      return setError('Enter your current password.');
    }
    const problem = passwordProblem(next);
    if (problem) return setError(problem);
    if (next !== confirm) return setError('The two passwords do not match.');

    setSaving(true);
    try {
      await setOwnPassword(member.id, next, hasPassword ? current : null);
      closeSheet();
      toast(hasPassword ? 'Password changed' : 'Password set — you can sign in with it now');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that password.');
      setSaving(false);
    }
  }

  return (
    <>
      <SheetHead
        title={hasPassword ? 'Change password' : 'Set a password'}
        sub="Used with your mobile number or email to sign in"
      />

      {hasPassword && (
        <Field label="Current password" required>
          <input
            type="password"
            value={current}
            autoComplete="current-password"
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
      )}

      <Field label="New password" required>
        <input
          type="password"
          value={next}
          autoComplete="off"
          onChange={(e) => setNext(e.target.value)}
        />
      </Field>

      <Field label="Confirm new password" required>
        <input
          type="password"
          value={confirm}
          autoComplete="off"
          onChange={(e) => setConfirm(e.target.value)}
        />
      </Field>

      <Hint>
        At least {PASSWORD_MIN} characters and one number. Forget it and{' '}
        <b>Forgot password</b> on the sign-in screen emails you a link to set a new one.
      </Hint>

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={save} disabled={saving}>
          {hasPassword ? 'Change password' : 'Set password'}
        </Button>
      </SheetActions>
    </>
  );
}
